import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { FavoriteUser } from '@/hooks/use-favorite-users';

export const useFavoriteUserMutations = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addFavorite = useMutation({
    mutationFn: async ({ favoriteUserId, position }: { favoriteUserId: string; position: number }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // First, check if user follows this person
      const { data: followData } = await supabase
        .from('followers')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', favoriteUserId)
        .maybeSingle();

      if (!followData) {
        throw new Error('You must follow this user before adding them to favorites');
      }

      // Call the reorder function to make space
      await supabase.rpc('reorder_user_favorites', {
        p_user_id: user.id,
        p_old_position: null,
        p_new_position: position
      });

      // Insert the new favorite
      const { data, error } = await supabase
        .from('user_favorites')
        .insert({
          user_id: user.id,
          favorite_user_id: favoriteUserId,
          position
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-users', user?.id] });
      toast({
        title: "Added to favorites",
        description: "User has been added to your top follows",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to add favorite",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const removeFavorite = useMutation({
    mutationFn: async ({ favoriteId, position }: { favoriteId: string; position: number }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Delete the favorite
      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('id', favoriteId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Call the reorder function to close the gap
      await supabase.rpc('reorder_user_favorites', {
        p_user_id: user.id,
        p_old_position: position,
        p_new_position: null
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-users', user?.id] });
      toast({
        title: "Removed from favorites",
        description: "User has been removed from your top follows",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove favorite",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const reorderFavorites = useMutation({
    mutationFn: async ({ favoriteId, oldPosition, newPosition }: { 
      favoriteId: string; 
      oldPosition: number; 
      newPosition: number; 
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Call the reorder function first
      await supabase.rpc('reorder_user_favorites', {
        p_user_id: user.id,
        p_old_position: oldPosition,
        p_new_position: newPosition
      });

      // Update the specific favorite's position
      const { error } = await supabase
        .from('user_favorites')
        .update({ position: newPosition })
        .eq('id', favoriteId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-users', user?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to reorder favorites",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const batchReorderFavorites = useMutation({
    mutationFn: async (reorderedFavorites: FavoriteUser[]) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Use simplified delete-and-insert approach
      const { error } = await supabase.rpc('batch_reorder_user_favorites', {
        p_user_id: user.id,
        p_favorites: reorderedFavorites.map(favorite => ({
          favorite_user_id: favorite.favorite_user_id
        }))
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-users', user?.id] });
      toast({
        title: "Changes saved",
        description: "Your favorites order has been updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save changes",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  return {
    addFavorite,
    removeFavorite,
    reorderFavorites,
    batchReorderFavorites,
  };
};